/**
 * **Mines** — the arm sweep, the
 * trigger predicate, and the ownerless detonation burst. Three pure, mostly-dice-free
 * rules-layer functions the room (`backend/src/encounterRoom.ts`) hand-chains at the SAME
 * two `dangerous-terrain` chain points (turn-end / post-move), exactly like {@link
 * "./meltdown".resolveMeltdownTick}/{@link "./meltdown".resolveMeltdownBlast} and {@link
 * "./resolve".resolveDangerousTerrainCheck} already do — a mine is a THIRD ownerless hazard
 * riding the same hand-chain shape, not a fourth bespoke pattern:
 *
 *   - {@link resolveMineArmSweep} — turn-end: flip `armed: false → true` for every mine
 *     `deployedBy` the just-ended combatant. Dice-free (a pure flag flip).
 *   - {@link resolveMineTriggers} — post-move: which **armed** mines does `moverId`'s move
 *     (from → to) newly trigger (Core p.106 — "triggered when any character enters an
 *     adjacent space… leaving an adjacent space will not trigger a mine")? Dice-free
 *     (pure enumeration); the room detonates each returned id in turn.
 *   - {@link resolveMineDetonation} — the ownerless BURST attack (Core p.106: "a mine
 *     creates a BURST attack starting from the space in which it was placed"), driven by
 *     the frozen {@link "@lancer-vtt/domain".MineProfile} payload. Mirrors {@link
 *     "./meltdown".resolveMeltdownBlast}'s shape (no attacker combatant to source, a
 *     damage packet per hit target, `resolve/damagePacket.ts`) but targets **every
 *     `isCharacter`** in range (friend AND foe, deployer included — no same-side exemption)
 *     rather than "every combatant."
 *
 * Two more are quick-action SYSTEMS-vs-DC checks mirroring {@link
 * "./resolve".resolveDangerousTerrainCheck}'s stat-vs-DC shape:
 *
 *   - {@link resolveMineDetection} — an AREA sweep within the searcher's Sensors, taking NO
 *     mine id (redaction means the client can't name a concealed mine). **The oracle
 *     constraint**: mine state is never read until AFTER the roll passes, so a failed sweep's
 *     emitted steps are byte-identical whether or not a mine exists anywhere in range.
 *   - {@link resolveMineDisarm} — entity-targeted (legitimate only because the disarmer's own
 *     projected state can only ever name a mine it's allowed to see). Success removes the
 *     mine; **failure detonates it** — reuses {@link resolveMineDetonation} verbatim, centered
 *     on the mine, catching the disarmer.
 *
 * {@link checkMineDetonateLegality} is the ownership gate for a **remote-only manual
 * detonation** (some MINE items, e.g. Thermite Charge, never use the proximity arm/trigger
 * sweep above at all; the deployer blows them on demand as a Quick action instead). The room
 * reuses {@link resolveMineDetonation} verbatim for the burst itself — only the trigger
 * condition and the legality gate differ. The suppression of the proximity sweep for such a
 * mine lives in {@link mineAdmitsTrigger} (`trigger: "remote"` refuses every mover) — see that
 * function's doc for why that site, not {@link resolveMineArmSweep}.
 *
 * Three optional widenings, all purely additive:
 *
 *   - **A DAMAGELESS mine.** {@link "@lancer-vtt/domain".MineProfile.damage} is optional;
 *     {@link resolveMineDetonation} skips the character-damage arm entirely (no
 *     roll/save/`DamageStep`/`hp_delta`) rather than dealing `0` — see that function's doc.
 *   - **An authorable trigger predicate.** {@link "@lancer-vtt/domain".MineProfile.trigger}
 *     `"ally"` narrows {@link resolveMineTriggers} to a mover on the deployer's own side (RAW's
 *     "unless otherwise specified," the Smoke Mine's case) — see {@link mineAdmitsTrigger}.
 *   - **An `area_add` emission path.** {@link "@lancer-vtt/domain".MineProfile.areaGrants}
 *     makes {@link resolveMineDetonation} emit a standing {@link
 *     "@lancer-vtt/domain".AreaEffect} sourced to the DETONATING character (RAW's "until the
 *     end of the detonating character's next turn") — the caller threads that detonator in as
 *     an argument, since `resolveMineDetonation` itself has no notion of who set the mine off.
 *     See that function's doc for the full account.
 */

import {
  applyResolved,
  type AreaAddStep,
  type AreaEffect,
  type Cell,
  type Combatant,
  type CombatantId,
  type DamageStep,
  type EncounterState,
  type ResolvedStep,
  type World,
  FLYING_STATUS,
  hasStatus,
  isCharacter,
  isObject,
  saveBonusFor,
  sideOf,
  worldFor,
} from "@lancer-vtt/domain";
import { type Result, ok, err, unwrap } from "@lancer-vtt/shared";
import { combatantRange, footprintCells, footprintDistance } from "./effects/spatialAnswerer";
import { moveWalk, walkEnters } from "./aura";
import { spawnObject } from "./spawn/object";
import { DANGEROUS_TERRAIN_DC, type DamageReport } from "./resolve";
import { dealDamagePacket } from "./resolve/damagePacket";
import type { LoadedOverlay } from "./effects/loader";
import { rollDamage, type DiceSource } from "../dice";
import { rollSelfCheck, selfCheckContext } from "./selfCheck";
import { saveVerdictText } from "./narrate/verdict";
import { interceptNonAttackDamage, liveCombatant, type NonAttackReactionSpend } from "./nonAttackIntercept";
import { interpretEffect } from "./effects/interpret";
import { mineDeployAtom } from "./effects/loader";
import { SEED_OVERLAY } from "./effects/seed";
import { NeedsDecisionSignal, promptingChoices, recordedChoices } from "./effects/choices";
import { saveNegatesDamage } from "./grunt";
import { reactionUsesNow } from "./events/gameEvents";
import type { EventLoop } from "./events/loop";

/** The fixed RAW trigger adjacency (Core p.106: "any character enters an adjacent space") — distinct from the authored {@link "@lancer-vtt/domain".MineProfile.burstRadius} detonation radius. */
export const MINE_TRIGGER_RANGE = 1;

/** True iff `c` is a placed, non-destroyed mine (`deployable` kind carrying a `mineProfile`). */
function isLiveMine(c: Combatant): boolean {
  return c.mineProfile !== undefined && c.pos !== null && c.pos !== undefined && !c.statuses.some((s) => s.name === "Destroyed");
}

/**
 * Does `mine`'s trigger predicate ADMIT `mover`? {@link "@lancer-vtt/domain".MineProfile.trigger}
 * `undefined` (the default) admits ANY `isCharacter` — no same-side exemption; the caller already
 * filtered to `isCharacter` before this runs. `"ally"` admits only a mover on the SAME side as the
 * mine's OWN {@link "@lancer-vtt/domain".MineProfile.deployedBy | deployer}.
 *
 * **`"remote"` admits NOBODY, ever, ANY side,** including the deployer itself walking back
 * adjacent — the mine's ONLY detonation path is the manual Quick action ({@link
 * checkMineDetonateLegality}/{@link resolveMineDetonation}). **This is the chosen enforcement
 * site over the alternative (teaching {@link resolveMineArmSweep} to skip arming a `"remote"`
 * mine):** this function's entire job is already "does the trigger admit this mover," so a
 * trigger kind that admits nobody is the single-guard fit — one more arm of the same dispatch,
 * not a new concern. Gating `armed` instead would overload that flag with a second meaning
 * ("past the walk-away window" AND "this trigger kind ever proximity-fires at all"), permanently
 * `false` for a live, working mine — misleading to a GM inspecting state — for a rule
 * `checkMineDetonateLegality` doesn't even key on (it deliberately does NOT require `armed`; see
 * that function's doc). One rule, one guard.
 *
 * **Resolved against the DEPLOYER, never `sideOf(mine)`.** `deployedBy` names a *combatant*
 * (strictly more precise than a side), and it is what makes the no-deployer case detectable at
 * all: an `"ally"`-triggered mine with no `deployedBy` (a GM-setup mine — no deploying combatant
 * to be allied to) fails CLOSED, never admitting. `sideOf(mine)` has no "nobody deployed this"
 * value to fail on.
 */
function mineAdmitsTrigger(state: EncounterState, mine: Combatant, mover: Combatant): boolean {
  if (mine.mineProfile?.trigger === "remote") return false; // never proximity-triggers — manual detonation is its sole path.
  if (mine.mineProfile?.trigger === "flying") return hasStatus(mover.statuses, FLYING_STATUS); // the Bouncing Mine: flyers only.
  if (mine.mineProfile?.trigger !== "ally") return true; // the default: any character admits.
  const deployerId = mine.mineProfile.deployedBy;
  if (deployerId === undefined) return false; // no deployer ⇒ no side to be allied with — fail closed.
  const deployer = state.combatants[deployerId];
  if (!deployer) return false; // defensive: the deployer is no longer in state.
  return sideOf(deployer) === sideOf(mover);
}

/**
 * The **turn-end arm sweep**: every un-armed mine {@link
 * "@lancer-vtt/domain".MineProfile.deployedBy | deployed by} `deployerId` arms now (the
 * deployer's turn just ended). Dice-free — a pure flag flip, mirroring {@link
 * "./meltdown".resolveMeltdownTick}'s "no dice, still a recorded resolution" shape.
 * Returns `null` when `deployerId` has no un-armed mines (nothing to arm) — the room
 * skips applying/broadcasting on `null`, exactly like every other hand-chain hook here.
 */
export function resolveMineArmSweep(
  state: EncounterState,
  deployerId: string,
): { steps: ResolvedStep[]; warnings: string[] } | null {
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  for (const id of Object.keys(state.combatants).sort()) {
    const c = state.combatants[id]!;
    if (c.mineProfile === undefined || c.mineProfile.armed) continue;
    if (c.mineProfile.deployedBy !== deployerId) continue;
    steps.push({ op: "mine_armed_set", target: id, armed: true, prev: false });
    warnings.push(`${c.name}: ARMED (deployer ${deployerId} ended their turn).`);
  }
  return steps.length === 0 ? null : { steps, warnings };
}

/**
 * The footprint a combatant of `size` would occupy anchored at `cell` — the hypothetical-
 * position analogue of {@link "./effects/spatialAnswerer".footprintCells}, which only reads
 * a combatant's CURRENT stored `pos`. Needed to measure the mover's PRE-move `from` anchor
 * (`applyMove` never writes `from` onto any combatant's live `pos`).
 */
function footprintAt(world: World<Cell>, cell: Cell, size: number): Cell[] {
  return world.footprint(cell, size).cells();
}

/**
 * The **post-move trigger sweep**: which **armed** mines does `moverId`'s just-resolved
 * move newly trigger? Core p.106's endpoint-based reading (no move path in this engine, the same
 * simplification the dangerous-terrain sweep makes): "mover was NOT within {@link
 * MINE_TRIGGER_RANGE} of the mine at `from`, IS at `to` (its current, already-committed
 * `pos`)." A mover already adjacent that stays adjacent does NOT re-trigger (Core p.106:
 * "leaving an adjacent space will not trigger a mine" — and never having left doesn't
 * either). `from === undefined` (the mover's first-ever placement, from unplaced)
 * reads as "not adjacent at from" — entering the board next to an armed mine can trigger it.
 *
 * **Trigger predicate: `isCharacter`, ANY side, deployer INCLUDED — the DEFAULT.**
 * Drones set off mines (they're characters); objects/other deployables/mines never do (not
 * `isCharacter`). There is deliberately **no same-side exemption** in this default — the
 * arm-at-turn-end delay IS the deployer's walk-away window, and RAW friendly fire is real. A
 * per-mine OVERRIDE ({@link "@lancer-vtt/domain".MineProfile.trigger}, checked via {@link
 * mineAdmitsTrigger}) can narrow it: a mine authored `trigger: "ally"` admits only a mover on the
 * deployer's own side, and an `undefined` field is the any-character default.
 *
 * **`reach: "over"`** (Webjaw Snare, Javelin Rockets) replaces the adjacency test: the mine fires
 * when the mover's walk ({@link moveWalk}: `move.path` when the move declared one, else `from` to
 * where it stands) steps into the mine's own space, the drone-aura entry walker, so crossing it
 * counts.
 *
 * Dice-free (pure enumeration) — returns the triggered mine ids, sorted for determinism;
 * the room resolves {@link resolveMineDetonation} on each in turn.
 */
export function resolveMineTriggers(state: EncounterState, moverId: string, move: { from: Cell | undefined; path?: readonly Cell[] | undefined }): string[] {
  const { from, path } = move;
  const mover = state.combatants[moverId];
  if (!mover || !mover.pos) return [];
  if (!isCharacter(mover)) return []; // only characters set off mines.
  if (mover.statuses.some((s) => s.name === "Destroyed")) return [];

  const world = worldFor(state);
  const moverFpAtTo = footprintCells(world, mover); // `mover.pos` already reflects the committed `to`.
  const moverFpAtFrom = from !== undefined ? footprintAt(world, from, mover.base.size) : [];

  const walk = moveWalk(mover, { from, path });
  const triggered: string[] = [];
  for (const id of Object.keys(state.combatants).sort()) {
    if (id === moverId) continue;
    const m = state.combatants[id]!;
    if (m.mineProfile === undefined || !m.mineProfile.armed || !isLiveMine(m)) continue;
    if (!mineAdmitsTrigger(state, m, mover)) continue; // an "ally"-only mine excludes a non-ally mover.
    const mineFp = footprintCells(world, m);
    if (m.mineProfile.reach === "over") {
      // Moving into or over the mine's own space, walked along the path (the drone-aura walker).
      const mineKeys = new Set(mineFp.map((c) => world.key(c)));
      if (walkEnters(walk, (at) => footprintAt(world, at, mover.base.size).some((c) => mineKeys.has(world.key(c))))) triggered.push(id);
      continue;
    }

    const rangeAtTo = footprintDistance(world, moverFpAtTo, mineFp);
    const wasAdjacent =
      moverFpAtFrom.length > 0 &&
      (footprintDistance(world, moverFpAtFrom, mineFp) ?? Infinity) <= MINE_TRIGGER_RANGE;
    const isAdjacentNow = rangeAtTo !== null && rangeAtTo <= MINE_TRIGGER_RANGE;

    if (!wasAdjacent && isAdjacentNow) triggered.push(id);
  }
  return triggered;
}

/**
 * The object a {@link "@lancer-vtt/domain".MineProfile.leaves} mine becomes when it goes off: a
 * revealed, non-obstructing deployable on the mine's space, with the authored name, HP and
 * Evasion, on the deployer's side and slot, keeping the mine's `deployedFrom`.
 */
function leftObject(mine: Combatant, at: Cell): Combatant {
  const leaves = mine.mineProfile!.leaves!;
  const built = unwrap(
    spawnObject({ combatantId: `${mine.id}:left`, name: leaves.name, size: 1, kind: "deployable", evasion: leaves.evasion, objectProfile: { blocksMovement: false, blocksSight: false, cover: "none" }, allegiance: sideOf(mine) }),
  );
  return {
    ...built,
    base: { ...built.base, maxHp: leaves.hp },
    current: { ...built.current, hp: leaves.hp },
    controllerSlotId: mine.controllerSlotId ?? null,
    ...(mine.deployedFrom !== undefined ? { deployedFrom: mine.deployedFrom } : {}),
    pos: at,
  };
}

/**
 * The mines that lapse as `unitId`'s turn starts: every live mine it deployed with
 * {@link "@lancer-vtt/domain".MineProfile.expires} `"deployer_turn_start"`, in id order. The room
 * removes each (Javelin Rockets: "until the start of your next turn").
 */
export function resolveMineExpiry(state: EncounterState, unitId: string): string[] {
  return Object.keys(state.combatants)
    .sort()
    .filter((id) => {
      const m = state.combatants[id]!;
      return m.mineProfile?.expires === "deployer_turn_start" && m.mineProfile.deployedBy === unitId;
    });
}

/** True when `c` carries the Destroyed status. */
function isDestroyed(c: Combatant): boolean {
  return c.statuses.some((s) => s.name === "Destroyed");
}

/** The prompt a single-target detonation asks its pick under. */
export function mineTargetPromptId(mineId: string): string {
  return `mine_target:${mineId}`;
}

/**
 * A single-target detonation's catch ({@link "@lancer-vtt/domain".MineProfile.singleTarget}): the
 * character the detonating side picks among `candidates` (the characters within the mine's
 * radius). One candidate is taken without asking and none catches nobody. Unanswered, an
 * interactive detonation halts on a combatant pick under {@link mineTargetPromptId}; otherwise it
 * takes the first candidate and says so.
 */
function pickSingleTarget(args: { state: EncounterState; mine: Combatant; candidates: string[]; choices?: Record<string, string>; interactive?: boolean }): { picked: string[]; note: string } {
  const { state, mine, candidates, choices, interactive } = args;
  if (candidates.length === 0) return { picked: [], note: `Mine ${mine.name}: no character within ${mine.mineProfile!.burstRadius} to catch.` };
  const prompt = mineTargetPromptId(mine.id);
  const answer = choices?.[prompt];
  const answered = candidates.length === 1 ? candidates[0] : answer !== undefined && candidates.includes(answer) ? answer : undefined;
  if (answered === undefined && interactive) {
    throw new NeedsDecisionSignal(prompt, `${mine.name}: pick the character it catches`, {
      options: candidates.map((id) => ({ id, label: state.combatants[id]!.name })),
      answerKind: "combatant",
    });
  }
  const pick = answered ?? candidates[0]!;
  const name = state.combatants[pick]!.name;
  return { picked: [pick], note: answered === undefined ? `Mine ${mine.name} catches ${name} (unanswered — took the first candidate).` : `Mine ${mine.name} catches ${name}.` };
}

/** The result of resolving a mine's detonation — mirrors {@link "./meltdown".MeltdownResult}'s shape (minus `detonated`, since the caller already knows it detonated). */
export type MineDetonationResult = {
  /** The grounded + witness steps to apply (an `apply_resolved`) — reveal (if concealed) + every hit target's save/mitigate/cascade steps. */
  steps: ResolvedStep[];
  /** Advisory notes (per-target save rolls, "no characters in range", …). */
  warnings: string[];
  /**
   * Every combatant the burst hit (a passed save still takes half; an object auto-hit counts), in
   * sweep order — the emitter's report the referee hands to `fireDamageTakenInto`
   * (`decisions/0175`), each with its post-mitigation amount. Absent when the burst hit nobody.
   */
  damaged?: DamageReport[];
  /** Reactions an Intercede on this burst spent (`nonAttackIntercept.ts`), for the caller's `spend_reaction` commands. */
  reactionSpentBy?: NonAttackReactionSpend[];
};

/** Named arguments for {@link resolveMineDetonation} (`max-params`-3 discipline). */
export type ResolveMineDetonationArgs = {
  state: EncounterState;
  mineId: string;
  dice: DiceSource;
  /**
   * The character whose action caused this detonation:
   * the mover who walked into it (the proximity trigger sweep), the deployer remotely
   * detonating it (the Detonate Mine Quick action), or the combatant whose disarm attempt
   * failed (Core p.106: "the mine detonates as normal," catching the disarmer). This is what
   * an `areaGrants` mine's created {@link "@lancer-vtt/domain".AreaEffect.source} binds to —
   * `resolveMineDetonation` itself has no notion of who set the mine off, so every caller
   * that wants area emission to work threads its own detonator in.
   *
   * `undefined` is tolerated — the character-damage/object-damage arms are UNCHANGED — but
   * an `areaGrants` mine then creates NO area at all (an honest no-op + warning), never a
   * phantom cloud sourced to the mine itself or a guessed combatant. See this function's doc.
   */
  detonatorId?: CombatantId;
  /**
   * Recorded prompt answers, keyed by prompt id — threaded to every per-target
   * {@link resolveDamageCascade} below, so a detonation that blows a structure point raises the
   * System Trauma "of your choice" pick to that target's owner (Core p.80) instead of the engine
   * auto-picking. Supplied by the `request_move` proximity-trigger path and the failed-disarm path
   * ({@link resolveMineDisarm}); absent for the remote Detonate Mine action and replay, which keep
   * the deterministic first-valid pick.
   */
  choices?: Record<string, string>;
  /** Interactive mode — see {@link choices}. An unrecorded pick throws `NeedsDecisionSignal` out through this function's caller. */
  interactive?: boolean;
  /**
   * The room's overlay, which each hit target's Resistance fold gathers from. Absent means
   * `SEED_OVERLAY`, which carries no pack NPC feature. The fold's attacker is the mine's
   * {@link "@lancer-vtt/domain".MineProfile.deployedBy}, not {@link detonatorId}: the detonator
   * of a proximity trigger is the mover who walked in, usually the victim itself, while the
   * deployer is the character whose mine it is. See `effects/nonAttackResistance.ts`.
   */
  overlay?: LoadedOverlay;
  /** The intent's event loop: the rolls, the damage packets and the cascades raise into it. Absent, they land with no event. */
  loop?: EventLoop;
};

/**
 * Resolve `mineId`'s **detonation** — the ownerless BURST attack Core p.106 describes,
 * driven entirely by the frozen {@link "@lancer-vtt/domain".MineProfile} payload (no
 * attacker combatant to source, mirroring {@link "./meltdown".resolveMeltdownBlast}'s shape).
 *
 * Every **`isCharacter`** within `mineProfile.burstRadius` (footprint-to-footprint,
 * {@link combatantRange}) is resolved — friend AND foe, the triggering mover and an adjacent
 * deployer included (friendly fire, no same-side filter) — via the SAME damage packet
 * (`resolve/damagePacket.ts`) the dangerous-terrain/meltdown paths raise: a
 * `mineProfile.saveAxis` hit rolls `d20 + saveBonusFor(target, saveAxis)` plus the target's folded save/check accuracy vs `saveDc` — the
 * axis the profile itself names, resolved per-axis (`decisions/0163`) (a pass halves the damage,
 * rounded up); an absent `saveAxis` auto-hits for full damage (Core p.106 default — "no
 * save is allowed" unless the mine's system says otherwise). `mineProfile.ap` threads straight
 * into that same packet, bypassing the target's Armor when set.
 *
 * **`isObject`** targets within the SAME burst are ALSO resolved — but ONLY when
 * `mineProfile.objectDamage` is set (a mine
 * lacking it leaves objects untouched, the exact prior behavior). Objects are always
 * **auto-hit, no save** (RAW: objects get no save to avoid damage at all), for the
 * SEPARATE `objectDamage` value rather than `damage` — Thermite Charge deals its
 * characters 3 Energy AP (save halves) and its objects a distinct, larger 10 Energy AP.
 * `mineProfile.ap` applies to the object hit too (one AP-ness per mine, not per hit-class).
 *
 * If the mine is currently `concealed`, the FIRST emitted step is a `concealed_set` reveal
 * (detonating is itself a reveal trigger); a non-concealed mine emits none (idempotent,
 * mirrors `clearHiddenSteps`' "no-op when already revealed" discipline). **Does NOT remove the
 * mine** — that is the room's job (a sibling `remove_combatant` in the same atomic `batch`, so
 * damage + reveal + removal undo/redo as ONE step).
 *
 * **Rolled damage.** {@link "@lancer-vtt/domain".MineProfile.damage}
 * may be a flat number (no roll, no extra step) or a dice spec (`{ count, sides }`, HEX Charges'
 * Explosive Mine:
 * `2d6`). A dice spec is rolled **once**, right after the reveal (if any) and before the
 * per-target loop — every target hit shares the SAME pre-mitigation roll, mirroring a weapon's
 * damage roll (never re-rolled per target) — and the draws are recorded in an emitted
 * `DamageStep` (`crit: false`, `armor: 0`, `resisted: false` — those fields describe THIS
 * roll record, not any one target's actual mitigation, which each target's damage packet below
 * computes and records in its own witness, with no dice). Recording the draws here is
 * load-bearing: the per-target witnesses carry only each target's share, so if the roll weren't
 * recorded here it would appear nowhere in the forward log and replay would have to re-roll
 * it — breaking byte-stability.
 *
 * **Detonation clauses** (`ll1-features` 5.1). A mine whose deploying `deploy_mine` authored
 * `on_fail`/`on_pass` ({@link "@lancer-vtt/domain".MineProfile.clauses}) catches characters even
 * with no damage, and after each caught character's save (and damage) runs the branch that save
 * picked, read off the entry its `deployedFrom` names (`mineDeployAtom`): `self` is the
 * deployer, `target` the character, `site` the mine's space, and the save DC the default for a
 * nested `force_save`. No save axis is an auto-hit, so `on_fail` runs. The clauses see the
 * pre-detonation state, as a `for_each` body does.
 *
 * **Single target** ({@link "@lancer-vtt/domain".MineProfile.singleTarget}): the catch is one
 * character picked among those within `burstRadius` ({@link pickSingleTarget}); objects are not hit.
 *
 * A missing/unplaced/non-mine `mineId` is an honest no-op (empty steps, an advisory warning) —
 * mirrors {@link "./meltdown".resolveMeltdownBlast}'s "no map position" tolerance.
 *
 * **A DAMAGELESS mine.** {@link
 * "@lancer-vtt/domain".MineProfile.damage} is OPTIONAL. When `undefined`, the entire
 * character-damage arm is skipped — no roll, no `save` step, no damage packet, no
 * `DamageStep`, no `hp_delta` for any `isCharacter` target — rather than the arm running with
 * a `damage: 0` (which would still emit a save and a zero `hp_delta`, a materially different,
 * dishonest step stream). {@link "@lancer-vtt/domain".MineProfile.objectDamage} is a fully
 * INDEPENDENT arm and is unaffected: a damageless mine may still hit objects if `objectDamage`
 * is set.
 *
 * **An `area_add` emission path.** {@link
 * "@lancer-vtt/domain".MineProfile.areaGrants}, when set, emits ONE {@link
 * "@lancer-vtt/domain".AreaAddStep} splicing a freshly-minted {@link
 * "@lancer-vtt/domain".AreaEffect} onto the mine's own cell — Core p.106's Smoke Mine case
 * (fills its Burst with soft cover, deals no damage at all), but nothing makes `areaGrants`
 * exclusive with `damage`/`objectDamage`: a mine may carry any combination. A mine with NONE
 * of the three payloads detonates reveal-only — legal at the domain/rules tier, but {@link
 * "./spawn".spawnMine} rejects authoring one: a detonation with no observable effect at all is a
 * content mistake, not a design.
 *
 * **The created area's radius reuses `mineProfile.burstRadius`** — deliberately no second
 * radius field (see {@link "@lancer-vtt/domain".MineProfile.areaGrants}'s doc for why).
 * **Its `source` is the DETONATING character (RAW: "until the end of the detonating
 * character's next turn")** — `resolveMineDetonation` has no notion of who set the mine off
 * (a bespoke, ownerless burst; contrast the DSL's `interpretCreateArea`, which always has an
 * activating combatant in `ctx`), so the caller threads it in as `args.detonatorId`. All
 * three real callers (the proximity trigger sweep, remote Detonate Mine, a failed disarm
 * attempt) have one in hand — see this module's header comment and each call site.
 * **`detonatorId` absent is an HONEST NO-OP for the area arm specifically** — no `area_add`
 * step, plus an advisory warning — never a phantom cloud sourced to the mine itself or a
 * guessed combatant; mirrors `interpretCreateArea`'s own unresolvable-origin posture. The
 * character-damage/object-damage arms are completely UNAFFECTED by an absent detonator.
 *
 * **Id minting is deterministic** (no `crypto.randomUUID`, mirroring `interpretCreateArea`
 * exactly) — keyed on the mine's own id (the natural carrier: a mine's cell is fixed at
 * deploy, and a detonated mine is removed in the SAME batch, so nothing else could ever mint
 * a second area off this same mine) plus its cell (`world.key`). `area_add`'s own
 * idempotent-by-presence discipline then makes a second detonation attempt at the same id a
 * no-op rather than a redundant record — moot in practice (a detonated mine is removed), but
 * free and consistent with the DSL primitive's own guarantee.
 *
 * **`appliedTurn` is stamped explicitly here, from `state.activation.turnOrdinal`** — never
 * left to an apply-time default: defaulting at apply time can't distinguish "a fresh emitter
 * that omitted it" from "an undo/redo replay of an instance that genuinely never had one," and
 * would break undo/redo byte-identity. This
 * makes `AreaEffect.appliedTurn`'s TWO creation sites (`interpretCreateArea` and this one)
 * both stamp unconditionally — see that field's own doc.
 */
export function resolveMineDetonation(args: ResolveMineDetonationArgs): MineDetonationResult {
  const { state, mineId, dice, detonatorId, choices, interactive, overlay, loop } = args;
  const mine = state.combatants[mineId];
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];

  if (!mine || mine.mineProfile === undefined || !mine.pos) {
    warnings.push(`Mine detonation: ${mineId} is not a placed mine — nothing to resolve.`);
    return { steps, warnings };
  }
  const minePos: Cell = mine.pos; // captured once, non-null (the guard above just confirmed it) — the area emission needs it as `origin`.
  const { burstRadius, damage: damageSpec, damageType, saveAxis, saveDc, ap, objectDamage, areaGrants } = mine.mineProfile;
  const apDamage = ap === true;

  if (mine.concealed) {
    steps.push({ op: "concealed_set", target: mineId, concealed: false, prev: true });
  }

  // Flat damage is used as-is — no roll, no extra step, the
  // byte-identical path every shipped mine (Thermite Charge) still takes. A dice spec rolls
  // ONCE for the whole burst and records the draws — see the doc comment above. 2.1: an
  // `undefined` spec (a damageless mine) rolls nothing and leaves `damage` `undefined` — the
  // per-target loop below reads that to skip the character-damage arm entirely.
  let damage: number | undefined;
  if (damageSpec === undefined) {
    // damageless (2.1) — no roll, no step, `damage` stays `undefined`.
  } else if (typeof damageSpec === "number") {
    damage = damageSpec;
  } else {
    const rolled = rollDamage({ source: dice, count: damageSpec.count, sides: damageSpec.sides, crit: false });
    const kept = rolled.draws.filter((_, i) => rolled.kept[i]).map((d) => d.value);
    damage = kept.reduce((a, b) => a + b, 0);
    const damageStep: DamageStep = {
      op: "damage",
      // `damageType` is guaranteed present here — this branch only runs when `damageSpec`
      // (hence `damage`) is authored, and `spawnMine`'s own construction-time guard rejects
      // any mine authoring `damage`/`objectDamage` with no `damageType` (damageType-optionality
      // follow-on to 2.4). A hand-built fixture bypassing `spawnMine` is the one way to violate
      // this — not reachable through any real content-authoring path.
      dtype: damageType!,
      dice: kept,
      sides: damageSpec.sides,
      bonus: 0,
      preMitigation: damage,
      ap: apDamage,
      armor: 0,
      resisted: false,
      final: damage,
      crit: false,
    };
    steps.push(damageStep);
  }

  const damaged: DamageReport[] = [];
  const spent: NonAttackReactionSpend[] = [];
  const selfCheck = selfCheckContext(state, overlay, { choices: interactive ? promptingChoices(choices) : recordedChoices(choices), loop });
  // A mine whose deploying atom authored `on_fail`/`on_pass` catches characters even with no
  // damage (the Mesmer Mine), and runs the branch its save picked against each one.
  const deployAtom = mine.mineProfile.clauses ? mineDeployAtom(mine, overlay ?? SEED_OVERLAY) : undefined;
  const catchesCharacters = damage !== undefined || mine.mineProfile.clauses === true;
  const inBurst = (id: string): boolean => {
    const range = combatantRange(state, mineId, id);
    return range !== null && range <= burstRadius;
  };
  // A mine set off by moving over it, or one that catches its detonator, catches only that character.
  const overCatch = mine.mineProfile.reach === "over" || mine.mineProfile.catches === "detonator" ? (id: string) => id === detonatorId : inBurst;
  const caughtCharacters = Object.keys(state.combatants)
    .sort()
    .filter((id) => id !== mineId && catchesCharacters && isCharacter(state.combatants[id]!) && !isDestroyed(state.combatants[id]!) && overCatch(id));
  const singlePick = mine.mineProfile.singleTarget ? pickSingleTarget({ state, mine, candidates: caughtCharacters, choices, interactive }) : undefined;
  if (singlePick !== undefined) warnings.push(singlePick.note);
  const caught = new Set(singlePick !== undefined ? singlePick.picked : caughtCharacters);
  // A mine that leaves an object spawns it now, on its own space; its clauses run with it as `self`.
  const left = mine.mineProfile.leaves !== undefined ? leftObject(mine, minePos) : undefined;
  if (left !== undefined) steps.push({ op: "spawn_object", combatant: left });
  const clauseSelf = left?.id ?? mine.mineProfile.deployedBy;
  let clauseRuns = 0;
  for (const id of Object.keys(state.combatants).sort()) {
    if (id === mineId) continue;
    // A protector who took an earlier victim's share takes its own from its current HP.
    const target = spent.length > 0 ? liveCombatant(state, steps, id) : state.combatants[id]!;
    const targetIsCharacter = caught.has(id);
    // Objects are hit ONLY when this mine authors an `objectDamage` value, and never by a
    // single-target detonation (it catches one character).
    const targetIsObject = objectDamage !== undefined && !mine.mineProfile.singleTarget && isObject(target) && !isDestroyed(target) && inBurst(id);
    if (!targetIsCharacter && !targetIsObject) continue;
    let dealt: number | undefined;
    let passed = false;
    if (targetIsCharacter) {
      if (saveAxis !== undefined) {
        // The axis this mine's own profile names (`mineProfile.saveAxis`), resolved
        // per-axis — a SYSTEMS mine really does roll SYSTEMS (`decisions/0163`). Until
        // 2026-08-28 every mine rolled the same flat `base.save` and the axis was a label.
        // The target's own save/check modifiers fold in (Power Up, Bonded, Stunned's auto-fail).
        // One roll serves both the damage halving and the clause branch.
        const dc = saveDc!; // spawnMine requires saveDc whenever saveAxis is set.
        const check = rollSelfCheck({ ctx: selfCheck, dice, combatant: target, against: saveAxis, bonus: saveBonusFor(target, saveAxis), dc, roll: "save" });
        steps.push(...check.steps);
        warnings.push(...check.warnings);
        const save = check.save;
        passed = save.result === "pass";
        // A Chaff Grunt that passes takes nothing, not half (`grunt.ts`).
        const negated = passed && saveNegatesDamage(target);
        dealt = damage === undefined || negated ? undefined : passed ? Math.ceil(damage / 2) : damage;
        warnings.push(
          `Mine ${mine.name} → ${target.name}: ${saveAxis} save ${saveVerdictText(save)} (${save.result})` +
            `${negated && damage !== undefined ? " — a Chaff Grunt takes no damage on a successful save" : passed && dealt !== undefined ? ` — halved to ${dealt}` : ""}.`,
        );
      } else {
        dealt = damage;
        warnings.push(`Mine ${mine.name} → ${target.name}: auto-hit (no save)${dealt !== undefined ? `, ${dealt} ${damageType}` : ""}.`);
      }
    } else {
      // Objects are ALWAYS auto-hit (no save) for the separate `objectDamage` value.
      dealt = objectDamage!;
      warnings.push(`Mine ${mine.name} → ${target.name}: object auto-hit (no save), ${dealt} ${damageType}.`);
    }

    if (dealt !== undefined) {
      // `damageType!`: guaranteed present — `spawnMine` rejects a `damage`/`objectDamage` payload
      // with no `damageType` (damageType-optionality follow-on to 2.4).
      // Resistance folds against the mine's deployer (see `overlay`'s doc). A GM-placed mine has no
      // deployer, so it folds in no-attacker mode: unconditional grants only.
      // A damage-Immune target (RAW p.68) takes nothing and is still reported, with `final: 0`.
      // Intercede (`nonAttackIntercept.ts`): a protector may take a character's share instead,
      // measured from the mine, and mitigates it with its own defenses.
      const intercept = targetIsCharacter
        ? interceptNonAttackDamage({ state, target, source: { combatant: mineId }, emitter: `mine.${mineId}`, amount: dealt, dtype: damageType!, choices, interactive, dice, overlay, spentReactions: new Set(spent.map((s) => s.combatantId)) })
        : undefined;
      let takerId = target.id;
      if (intercept) {
        spent.push(intercept.spend);
        steps.push(...intercept.steps);
        warnings.push(...intercept.warnings);
        takerId = intercept.protectorId;
      }
      // The damage packet (`resolve/damagePacket.ts`) lands on the state the burst's earlier
      // steps left, so a protector who already took a share has the Overshield and HP it left.
      const landed = dealDamagePacket({
        packet: { kind: "damage_packet", source: mine.mineProfile.deployedBy ?? null, target: takerId, instances: [{ dtype: damageType!, amount: dealt }], fromAttack: false, ap: apDamage },
        state: steps.length > 0 ? applyResolved(state, steps) : state,
        dice,
        overlay,
        choices,
        interactive,
        selfCheck,
        loop,
      });
      steps.push(...landed.steps);
      warnings.push(...landed.warnings);
      damaged.push(landed.report);
    }

    if (targetIsCharacter && deployAtom !== undefined) {
      const branch = passed ? deployAtom.on_pass : deployAtom.on_fail;
      if (branch !== undefined && branch.length > 0) {
        clauseRuns++;
        const ran = interpretEffect(branch, {
          state,
          actors: { ...(clauseSelf !== undefined ? { self: clauseSelf } : {}), target: id },
          dice,
          site: minePos,
          overlay: overlay ?? SEED_OVERLAY,
          ...(saveDc !== undefined ? { defaultSaveDc: saveDc } : {}),
          ...(mine.deployedFrom !== undefined ? { activatingEntry: mine.deployedFrom.entryId } : {}),
          ...(passed && saveNegatesDamage(target) ? { saveNegatesDamageFor: id } : {}),
          ...(interactive ? { interactive: true, choices: promptingChoices(choices) } : choices !== undefined ? { choices: recordedChoices(choices) } : {}),
        });
        steps.push(...ran.steps);
        warnings.push(...ran.warnings);
      }
    }
  }
  if (damaged.length === 0 && clauseRuns === 0) {
    // 2.1: a mine carrying NEITHER `damage` NOR `objectDamage` has no hit arm to report a
    // miss for — say so honestly rather than implying it would have hit a character present.
    // 2.3: an `areaGrants` mine (the Smoke Mine's exact shape) isn't "reveal only" even with
    // zero hits — it still paints an area below — so that phrasing is reserved for a mine
    // with NO payload at all (spawnMine now rejects authoring one, but a hand-built fixture
    // can still reach this path).
    warnings.push(
      damage !== undefined || objectDamage !== undefined || mine.mineProfile.clauses
        ? `Mine detonation: no characters within BURST ${burstRadius} of ${mine.name}.`
        : areaGrants !== undefined
          ? `Mine detonation: ${mine.name} carries no damage payload — area only.`
          : `Mine detonation: ${mine.name} carries no damage payload — reveal only.`,
    );
  }

  // An `areaGrants` mine creates a standing AreaEffect on
  // its own cell, sourced to the DETONATING character — see this function's own doc for the
  // full account of id-minting, `appliedTurn`, and the absent-detonator posture.
  if (areaGrants !== undefined) {
    if (detonatorId === undefined) {
      warnings.push(
        `Mine detonation: ${mine.name} carries an area payload but no detonating character was given — no area created.`,
      );
    } else {
      const world = worldFor(state);
      const areaId = `${mineId}:area:${world.key(minePos)}`;
      if (state.areaEffects?.[areaId] === undefined) {
        const area: AreaEffect = {
          id: areaId,
          origin: minePos,
          shape: "disk",
          size: burstRadius,
          grants: areaGrants,
          source: detonatorId,
          expiry: { kind: "turn", edge: "end", who: "source" },
          appliedTurn: state.activation.turnOrdinal,
        };
        const step: AreaAddStep = { op: "area_add", area };
        steps.push(step);
      }
    }
  }

  return { steps, warnings, ...(damaged.length > 0 ? { damaged } : {}), ...(spent.length > 0 ? { reactionSpentBy: reactionUsesNow(spent)! } : {}) };
}

// ── detection + disarm — quick-action SYSTEMS-vs-DC checks ──────────────────────────────────

/**
 * The SYSTEMS-check DC for both mine detection and disarm (Core p.106 states no explicit
 * number — "a successful SYSTEMS check" — mirroring {@link
 * "./resolve".DANGEROUS_TERRAIN_DC}'s same "no RAW number given" situation, which resolved to
 * the base save floor **10**). A named alias (not a bare re-export) so a future divergence
 * between the two checks' DCs is a one-line change, not a search-and-replace.
 */
export const MINE_SYSTEMS_DC = DANGEROUS_TERRAIN_DC;

/** A deploy/detect/disarm verdict — `Result<void>`, mirroring `pilotBridge.ts`'s `BridgeVerdict`. */
export type MineCheckVerdict = Result<void>;

/** The result of a detection sweep or a disarm attempt — the SYSTEMS-check steps + advisory notes. */
export type MineCheckResult = {
  /** The grounded steps to apply (an `apply_resolved`) — always at least the `save` step. */
  steps: ResolvedStep[];
  warnings: string[];
};

/**
 * Resolve `actorId`'s mine **detection sweep** (Core p.106): an AREA-shaped SYSTEMS-vs-DC
 * check within `actorId`'s own Sensors — no mine id, ever (redaction means the caller
 * can't have one to give). **THE ORACLE CONSTRAINT (load-bearing):** the roll (`d20 +
 * actorId.base.systems` vs {@link MINE_SYSTEMS_DC}) is drawn and recorded FIRST, unconditionally
 * — mine state is not read at all until AFTER `passed` is known, so a FAILED sweep's `steps`
 * are shaped identically ([the `save` step], nothing else) whether zero, one, or several hidden
 * mines sit within Sensors range: no branch in this function's failure path ever inspects
 * mine presence, so there is no shape to leak it through. A PASS additionally emits a
 * `concealed_set` reveal for every concealed, placed mine within Sensors range (footprint-to-
 * footprint, {@link combatantRange} ≤ `actorId.base.sensors`) — sorted ids, deterministic order.
 *
 * `null` when `actorId` is unknown/unplaced (nothing to sweep from) — the room's own legality
 * gate should reject this before calling in, but this stays honest-tolerant like {@link
 * resolveMineDetonation}'s unknown-mine case.
 */
export function resolveMineDetection(args: ResolveMineDetectionArgs): MineCheckResult | null {
  const { state, actorId, dice, overlay, loop } = args;
  const actor = state.combatants[actorId];
  if (!actor || !actor.pos) return null;

  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];

  // The searcher's own save/check modifiers fold in (Power Up, Bonded, Bolster): they read only the
  // searcher, never mine state, so the oracle constraint below holds.
  const check = rollSelfCheck({ ctx: selfCheckContext(state, overlay, { loop }), dice, combatant: actor, against: "systems", bonus: actor.base.systems ?? 0, dc: MINE_SYSTEMS_DC, roll: "check" });
  steps.push(...check.steps);
  warnings.push(...check.warnings);
  const passed = check.save.result === "pass";

  if (!passed) {
    // No further reads of mine state — this is the whole oracle guarantee: a miss's `steps`
    // depend ONLY on the searcher's own roll, never on what (if anything) is out there.
    warnings.push(`${actor.name}: SYSTEMS ${saveVerdictText(check.save)} (fail) — detects nothing.`);
    return { steps, warnings };
  }

  const sensors = actor.base.sensors ?? 0;
  let revealed = 0;
  for (const id of Object.keys(state.combatants).sort()) {
    const c = state.combatants[id]!;
    if (!isLiveMine(c) || c.concealed !== true) continue;
    const range = combatantRange(state, actorId, id);
    if (range === null || range > sensors) continue;
    steps.push({ op: "concealed_set", target: id, concealed: false, prev: true });
    revealed++;
  }
  warnings.push(
    `${actor.name}: SYSTEMS ${saveVerdictText(check.save)} (pass) — ` +
      (revealed > 0 ? `revealed ${revealed} mine(s) within Sensors ${sensors}.` : `nothing within Sensors ${sensors}.`),
  );
  return { steps, warnings };
}

/**
 * May `actorId` attempt to disarm `mineId` (Core p.106: "disarmed from adjacent spaces")?
 * The mine must exist, be placed, and not already Destroyed; `actorId` must be adjacent
 * (Range 1). Deliberately does NOT require the mine to be already revealed — the deployer
 * legitimately disarms its own still-concealed mine (reclaiming a trap nobody else ever
 * needs to learn existed); redaction is what stops anyone else from ever NAMING a
 * concealed mine's id in the first place, so this check needs no `concealed` gate of its own.
 */
export function checkMineDisarmLegality(state: EncounterState, actorId: CombatantId, mineId: CombatantId): MineCheckVerdict {
  const actor = state.combatants[actorId];
  if (!actor) return err(`disarm: no such combatant ${actorId}`);
  if (!actor.pos) return err(`disarm: ${actor.name} isn't placed on the map`);
  const mine = state.combatants[mineId];
  if (!mine || mine.mineProfile === undefined) return err(`disarm: ${mineId} is not a mine`);
  if (!isLiveMine(mine)) return err(`disarm: ${mine.name} is no longer on the map`);
  const dist = combatantRange(state, actorId, mineId);
  if (dist !== 1) return err(`disarm: ${actor.name} must be adjacent to ${mine.name} (got distance ${dist ?? "unreachable"})`);
  return ok(undefined);
}

/**
 * May `actorId` **remotely detonate** `mineId` (MINE
 * tag Core Book p.106)? Some MINE items (Thermite Charge: "This charge must be remotely
 * detonated as a quick action") never use the proximity arm/trigger sweep at all — this is
 * their ONLY detonation path, gated purely on **ownership**, never adjacency/range (RAW:
 * "remotely"). The mine must exist, be placed, not already Destroyed, AND be `mineId`'s own
 * `mineProfile.deployedBy === actorId` — server-verified against the mine's frozen field,
 * never a wire-claimed ownership. Deliberately does NOT require `armed` — a manual remote
 * detonation is a distinct verb from the proximity trigger the `armed` flag gates; a mine
 * the deployer just placed this same turn can still be blown remotely.
 */
export function checkMineDetonateLegality(state: EncounterState, actorId: CombatantId, mineId: CombatantId): MineCheckVerdict {
  const actor = state.combatants[actorId];
  if (!actor) return err(`detonate: no such combatant ${actorId}`);
  const mine = state.combatants[mineId];
  if (!mine || mine.mineProfile === undefined) return err(`detonate: ${mineId} is not a mine`);
  if (!isLiveMine(mine)) return err(`detonate: ${mine.name} is no longer on the map`);
  if (mine.mineProfile.deployedBy !== actorId) {
    return err(`detonate: ${actor.name} did not deploy ${mine.name} — only the deployer can remotely detonate it`);
  }
  return ok(undefined);
}

/** Named arguments for {@link resolveMineDetection}. */
export type ResolveMineDetectionArgs = {
  state: EncounterState;
  actorId: CombatantId;
  dice: DiceSource;
  /** The room's overlay, which the searcher's SYSTEMS check folds its save/check modifiers from. */
  overlay?: LoadedOverlay;
  /** The intent's event loop: the rolls, the damage packets and the cascades raise into it. Absent, they land with no event. */
  loop?: EventLoop;
};

/** Named arguments for {@link resolveMineDisarm} (`max-params`-3 discipline). */
export type ResolveMineDisarmArgs = {
  state: EncounterState;
  actorId: CombatantId;
  mineId: CombatantId;
  dice: DiceSource;
  /** Threaded to a botched disarm's {@link resolveMineDetonation} — see {@link ResolveMineDetonationArgs.overlay}. */
  overlay?: LoadedOverlay;
  /** Threaded to a botched disarm's {@link resolveMineDetonation} — see {@link ResolveMineDetonationArgs.choices}. */
  choices?: Record<string, string>;
  /** Threaded to a botched disarm's {@link resolveMineDetonation} — see {@link ResolveMineDetonationArgs.interactive}. */
  interactive?: boolean;
  /** The intent's event loop: the rolls, the damage packets and the cascades raise into it. Absent, they land with no event. */
  loop?: EventLoop;
}

/**
 * Resolve `actorId`'s **disarm attempt** on `mineId`: a SYSTEMS-vs-DC check (`d20 +
 * actorId.base.systems` vs {@link MINE_SYSTEMS_DC}), recorded first. On a **pass**, `steps` is
 * just the `save` — a clean disarm, no burst. On a **fail**, Core p.106: "the mine detonates
 * as normal" — `steps` is the `save` PLUS the IDENTICAL {@link resolveMineDetonation} burst
 * (reveal-if-concealed + every hit character's save/mitigate/cascade), catching the disarmer
 * itself if it's within the mine's `burstRadius` (it's adjacent by the legality gate, so
 * unless `burstRadius` is 0 it always is). Either way the mine is SPENT (disarmed or
 * detonated) — `passed` is advisory for the room's messaging; the room fires
 * `remove_combatant` UNCONDITIONALLY, mirroring {@link resolveMineDetonation}'s own callers.
 */
export function resolveMineDisarm(args: ResolveMineDisarmArgs): (MineCheckResult & { passed: boolean; damaged?: DamageReport[]; reactionSpentBy?: NonAttackReactionSpend[] }) | null {
  const { state, actorId, mineId, dice, overlay, choices, interactive, loop } = args;
  const actor = state.combatants[actorId];
  const mine = state.combatants[mineId];
  if (!actor || !mine) return null;

  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];

  // The disarmer's own save/check modifiers fold in (Power Up, Bonded, Bolster).
  const check = rollSelfCheck({ ctx: selfCheckContext(state, overlay, { loop }), dice, combatant: actor, against: "systems", bonus: actor.base.systems ?? 0, dc: MINE_SYSTEMS_DC, roll: "check" });
  steps.push(...check.steps);
  warnings.push(...check.warnings);
  const passed = check.save.result === "pass";

  if (passed) {
    warnings.push(`${actor.name}: SYSTEMS ${saveVerdictText(check.save)} (pass) — ${mine.name} disarmed.`);
    return { steps, warnings, passed: true };
  }

  warnings.push(`${actor.name}: SYSTEMS ${saveVerdictText(check.save)} (fail) — ${mine.name} detonates!`);
  // 2.3: the botched disarmer IS the detonating character (Core p.106: "the mine detonates
  // as normal," catching the disarmer) — RAW's area `source` binds to `actorId`, not `mine`'s
  // deployer.
  const detonation = resolveMineDetonation({ state, mineId, dice, detonatorId: actorId, overlay, choices, interactive, loop });
  steps.push(...detonation.steps);
  warnings.push(...detonation.warnings);
  return { steps, warnings, passed: false, ...(detonation.damaged !== undefined ? { damaged: detonation.damaged } : {}), ...(detonation.reactionSpentBy !== undefined ? { reactionSpentBy: detonation.reactionSpentBy } : {}) };
}
